// src/components/lead-tracker/EditGoalsDialog.jsx
// Owner-only per-year tracker goals. Percent fields are edited as whole
// numbers (70 means 70%). Saved to lead_tracker_settings/{ownerUid}.
import React, { useState } from "react";
import { toast } from "react-toastify";
import { X } from "lucide-react";
import { saveTrackerGoals } from "../../utils/leadTrackerStore";

// Module-scope so its identity is stable across re-renders. Defining this
// inside the dialog body made React remount the <input> on every keystroke,
// dropping keyboard focus after each typed character.
function Field({ label, value, suffix, onChange }) {
  return (
    <label className="block text-xs text-slate-600 mb-2">
      {label}
      <span className="flex items-center gap-1 mt-1">
        <input
          type="number"
          min="0"
          className="w-24 border border-slate-300 rounded-lg px-2 py-1.5 text-sm"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
        {suffix && <span className="text-slate-400">{suffix}</span>}
      </span>
    </label>
  );
}

export default function EditGoalsDialog({ year, goals, ownerId, onClose, onSaved }) {
  const [form, setForm] = useState({
    monthlyLeadsGoal: goals.monthlyLeadsGoal,
    closingsGoal: goals.closingsGoal,
    leadToApptPct: Math.round(goals.leadToApptPct * 100),
    apptToContractPct: Math.round(goals.apptToContractPct * 100),
    contractToClosePct: Math.round(goals.contractToClosePct * 100),
  });
  const [saving, setSaving] = useState(false);
  const num = (v) => Number(v);

  const save = async () => {
    // A blank field used to save as 0, silently zeroing a goal. Require a
    // real number in every field instead.
    const blankOrBad = Object.values(form).some(
      (v) => v === "" || v === null || v === undefined || !Number.isFinite(Number(v)) || Number(v) < 0,
    );
    if (blankOrBad) {
      toast.error("Please enter a number of 0 or more in every goal field");
      return;
    }
    setSaving(true);
    const next = {
      monthlyLeadsGoal: num(form.monthlyLeadsGoal),
      closingsGoal: num(form.closingsGoal),
      leadToApptPct: num(form.leadToApptPct) / 100,
      apptToContractPct: num(form.apptToContractPct) / 100,
      contractToClosePct: num(form.contractToClosePct) / 100,
    };
    try {
      await saveTrackerGoals(ownerId, year, next);
      onSaved(next);
      toast.success(`Goals saved for ${year}`);
      onClose();
    } catch (e) {
      toast.error("Couldn't save goals");
      setSaving(false);
    }
  };

  const setField = (k) => (v) => setForm((prev) => ({ ...prev, [k]: v }));

  return (
    <div className="fixed inset-0 z-[100] bg-black/30 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-slate-900">Goals for {year}</h3>
          <button onClick={onClose}><X className="w-4 h-4 text-slate-400" /></button>
        </div>
        <Field label="Monthly Leads Goal" value={form.monthlyLeadsGoal} onChange={setField("monthlyLeadsGoal")} />
        <Field label="Monthly closings goal" value={form.closingsGoal} onChange={setField("closingsGoal")} />
        <Field label="Lead to Appointment goal" value={form.leadToApptPct} suffix="%" onChange={setField("leadToApptPct")} />
        <Field label="Appointment to Contract goal" value={form.apptToContractPct} suffix="%" onChange={setField("apptToContractPct")} />
        <Field label="Contract to Close goal" value={form.contractToClosePct} suffix="%" onChange={setField("contractToClosePct")} />
        <button
          disabled={saving}
          onClick={save}
          className="w-full mt-2 bg-slate-800 text-white rounded-lg py-2 text-sm font-semibold hover:bg-slate-700 disabled:opacity-50"
        >
          {saving ? "Saving..." : "Save goals"}
        </button>
      </div>
    </div>
  );
}
